import LandingLayout from "@/components/LandingLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { motion } from "framer-motion";
import {
  Bot,
  Brain,
  Check,
  Command,
  Kanban,
  MessageSquareCode,
  Sparkles,
  Zap
} from "lucide-react";
import { TbServer } from "react-icons/tb";
import { useLocation, useNavigate } from 'react-router-dom';

import { useEffect, useState } from "react";

import heroMonitorScreenshot from "@/assets/statusbot-monitor-details-new.png";
import heroPlanScreenshot from "@/assets/statusbot-plan-board-new.png";

const features = [
  {
    icon: <Command className="w-8 h-8 text-violet-500" />,
    title: "Command Bar AI",
    description: "Access AI assistance anywhere with CMD+K. Get instant answers, create tasks, and analyze projects effortlessly."
  },
  {
    icon: <Brain className="w-8 h-8 text-violet-500" />,
    title: "AI Task Analysis",
    description: "Let AI help break down complex tasks, suggest improvements, and identify potential blockers before they happen."
  },
  {
    icon: <Kanban className="w-8 h-8 text-violet-500" />,
    title: "Flexible Project Views",
    description: "Switch seamlessly between board and list views. Organize tasks your way with custom filters and grouping."
  },
  {
    icon: <Bot className="w-8 h-8 text-violet-500" />,
    title: "Service Monitoring",
    description: "Keep your services healthy with AI-powered monitoring. Get intelligent alerts and predictive insights."
  },
  {
    icon: <MessageSquareCode className="w-8 h-8 text-violet-500" />,
    title: "Smart Planning",
    description: "AI-powered insights for weekly planning, task estimation, and resource allocation."
  },
  {
    icon: <Zap className="w-8 h-8 text-violet-500" />,
    title: "Lightning Fast",
    description: "Blazingly fast interface with keyboard shortcuts and instant search for maximum productivity."
  }
];

const rotatingTextsConfig = [
  { text: "Project Planning", color: "text-violet-500", cursorColor: "bg-violet-500" },
  { text: "Service Monitoring", color: "text-emerald-500", cursorColor: "bg-emerald-500" },
  { text: "Team Collaboration", color: "text-blue-500", cursorColor: "bg-blue-500" },
  { text: "Task Tracking", color: "text-orange-500", cursorColor: "bg-orange-500" },
];

const Landing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);

  useEffect(() => {
    const text = rotatingTextsConfig[currentTextIndex].text;
    let timeouts = [];

    const animateText = async () => {
      setIsTyping(true);

      // Clear existing text
      setDisplayText('');

      // Type out the text
      for (let i = 0; i <= text.length; i++) {
        const timeout = setTimeout(() => {
          setDisplayText(text.slice(0, i));
        }, i * 100); // Type speed
        timeouts.push(timeout);
      }

      // Pause at the end
      const pauseTimeout = setTimeout(() => {
        setIsTyping(false);
        // Delete text
        for (let i = text.length; i >= 0; i--) {
          const timeout = setTimeout(() => {
            setDisplayText(text.slice(0, i));
          }, (text.length - i) * 50); // Delete speed
          timeouts.push(timeout);
        }

        // Move to next text
        const nextTimeout = setTimeout(() => {
          setCurrentTextIndex((prev) => (prev + 1) % rotatingTextsConfig.length);
        }, text.length * 50 + 100);
        timeouts.push(nextTimeout);
      }, text.length * 100 + 2000); // Wait time after typing

      timeouts.push(pauseTimeout);
    };

    animateText();

    // Cleanup function
    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, [currentTextIndex]);

  useEffect(() => {
    if (location.state?.scrollTo) {
      const element = document.getElementById(location.state.scrollTo);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location.state]);

  return (
    <LandingLayout>
      {/* Hero Section - Reduced mobile spacing */}
      <section className="px-4 py-4 sm:py-6 md:py-10">
        <motion.div className="sm:container mx-auto text-center">
          <Card className="pt-8 sm:pt-16 md:pt-28 pb-4 sm:pb-6 md:pb-8 px-0 sm:px-4 md:px-8 border-2 border-violet-500/20">
            <CardContent className="space-y-4 sm:space-y-6 md:space-y-8">
              {/* Badge - Reduced padding */}
              <div className="flex items-center justify-center">
                <span className="px-3 py-1 sm:px-4 sm:py-1.5 bg-violet-500/10 text-violet-500 rounded-full text-xs sm:text-sm font-medium flex items-center">
                  <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                  AI-Driven Operations
                </span>
              </div>

              {/* Heading - Adjusted text sizes */}
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold tracking-tight">
                <div className="relative h-[1.5em] overflow-hidden inline-flex items-center">
                  <span className={rotatingTextsConfig[currentTextIndex].color}>
                    {displayText}
                    <span className={`inline-block w-[4px] sm:w-[6px] h-[1em] ${rotatingTextsConfig[currentTextIndex].cursorColor} animate-pulse ml-1`}>
                      &nbsp;
                    </span>
                  </span>
                </div>
                <span className="block sm:inline ml-1 sm:ml-2">with AI at Your Fingertips</span>
              </h1>

              {/* Subheading - Adjusted text and spacing */}
              <p className="text-xs sm:text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
                The modern project management tool that combines powerful planning features with AI assistance. Press <span className="inline-flex items-center border rounded px-1 sm:px-1.5 py-0.5 text-xs sm:text-sm bg-muted/50"><Command className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-0.5" />K</span> anywhere to unlock AI superpowers.
              </p>

              {/* Buttons - Adjusted spacing */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 justify-center px-4 sm:px-0">
                <Button size="default" sm:size="lg" onClick={() => navigate('/waitlist')}>
                  Join the Waitlist
                </Button>
                {/* <Button size="default" sm:size="lg" variant="outline" onClick={() => navigate('/features')}>
                  See it in Action
                </Button> */}
              </div>

              {/* Screenshot - Adjusted padding */}
              <div className="pt-4 sm:pt-8 md:pt-16 flex justify-center px-0 sm:px-2 md:px-8">
                <img
                  src={heroPlanScreenshot}
                  alt="Screenshot of the project planning interface"
                  className="w-full rounded-lg border-2 border-violet-500/20 shadow-md shadow-violet-500/10 dark:shadow-violet-500/5"
                />
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </section>

      {/* Monitoring Section */}
      <section className="px-4 py-12 sm:py-20 bg-violet-500/5">
        <motion.div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div className="space-y-6">
              <div className="flex items-center">
                <span className="px-4 py-1.5 bg-violet-500/10 text-violet-500 rounded-full text-sm font-medium flex items-center">
                  <TbServer className="w-4 h-4 mr-2" />
                  Integrated Monitoring
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold">
                Keep Your Services Healthy with AI-Powered Monitoring
              </h2>
              <p className="text-xl text-muted-foreground">
                Monitor your web services, APIs, and infrastructure in the same place you plan your projects. Get intelligent alerts and predictive insights before issues impact your users.
              </p>
              <ul className="space-y-4">
                {[
                  "Real-time monitoring of endpoints and services",
                  "AI-powered anomaly detection and predictive alerts",
                  "Automatic incident creation and task assignment",
                  "Historical performance analytics and trends",
                ].map((item) => (
                  <li key={item} className="flex items-center space-x-3">
                    <Sparkles className="w-5 h-5 text-violet-500" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:pl-8">
              <img
                src={heroMonitorScreenshot}
                alt="Service monitoring dashboard"
                className="rounded-lg border-2 border-violet-500/20 shadow-md shadow-violet-500/10 dark:shadow-violet-500/5"
              />
            </div>
          </div>
        </motion.div>
      </section>

      {/* Features Grid */}
      <section id="features" className="px-4 py-12 sm:py-20">
        <motion.div className="container mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12">
            Project management that works for you
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
              >
                <Card className="p-6 hover:shadow-lg hover:shadow-violet-500/10 transition-all border-2 border-violet-500/20">
                  <CardContent className="space-y-4 pt-4">
                    {feature.icon}
                    <h3 className="text-xl font-semibold">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="px-4 py-12 sm:py-20 bg-violet-500/5">
        <motion.div className="container mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-8 sm:mb-12">
            Simple, transparent pricing
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            <Card className="p-6 hover:shadow-lg hover:shadow-violet-500/10 transition-all border-2 border-violet-500/20">
              <CardContent className="space-y-4 pt-4">
                <h3 className="text-xl font-semibold">Free</h3>
                <div className="space-y-1 flex items-center justify-center">
                  <div className="text-3xl font-bold">$0</div>
                  <p className="text-sm text-muted-foreground ml-3">/ user / month</p>
                </div>
                <p className="text-muted-foreground">Perfect for personal projects</p>
                <ul className="space-y-2 text-left">
                  <li className="flex items-center">
                    <Check className="w-4 h-4 mr-2 text-violet-500" />
                    Up to 3 projects
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 mr-2 text-violet-500" />
                    Basic AI features
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 mr-2 text-violet-500" />
                    Community support
                  </li>
                </ul>
                <div>
                    <Button className="w-full mt-4" variant="secondary" disabled>
                        Coming Soon
                    </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="p-6 hover:shadow-lg hover:shadow-violet-500/10 transition-all border-2 border-violet-500">
              <CardContent className="space-y-4 pt-4">
                <h3 className="text-xl font-semibold">Pro</h3>
                <div className="space-y-1 flex items-center justify-center">
                  <div className="text-3xl font-bold">$19</div>
                  <p className="text-sm text-muted-foreground ml-3">/ user / month</p>
                </div>
                <p className="text-muted-foreground">For growing teams</p>
                <ul className="space-y-2 text-left">
                  <li className="flex items-center">
                    <Check className="w-4 h-4 mr-2 text-violet-500" />
                    Unlimited projects
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 mr-2 text-violet-500" />
                    Advanced AI features
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 mr-2 text-violet-500" />
                    Priority support
                  </li>
                </ul>
                <div>
                    <Button className="w-full mt-4" variant="secondary" disabled>
                        Coming Soon
                    </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="p-6 hover:shadow-lg hover:shadow-violet-500/10 transition-all border-2 border-violet-500/20">
              <CardContent className="space-y-4 pt-4">
                <h3 className="text-xl font-semibold">Enterprise</h3>
                <div className="text-3xl font-bold">Custom</div>
                <p className="text-muted-foreground">For large organizations</p>
                <ul className="space-y-2 text-left">
                  <li className="flex items-center">
                    <Check className="w-4 h-4 mr-2 text-violet-500" />
                    Custom integrations
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 mr-2 text-violet-500" />
                    Dedicated support
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 mr-2 text-violet-500" />
                    SLA guarantees
                  </li>
                </ul>
                <div>
                    <Button className="w-full mt-4" onClick={() => window.location.href = 'mailto:enterprise@status.bot'}>
                        Contact Us
                    </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </motion.div>
      </section>
    </LandingLayout>
  );
};

export default Landing;
